import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  ViewContainerRef
} from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatError,
  MatFormField,
  MatHint,
  MatLabel,
  MatSuffix
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { format, parse } from 'date-fns';
import moment from 'moment';
import {
  NgxMatTimepickerComponent,
  NgxMatTimepickerModule
} from 'ngx-mat-timepicker';
import { Observable, of } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { BookingItemPriceUpdateService } from 'src/app/shared/dialogs/hire-price-structure-dialog/booking-item-price-update.service';
import { HIREABLE_PRICE_UPDATE_IJ } from 'src/app/shared/dialogs/hire-price-structure-dialog/hireable-price-update';
import { EDIT_BOOKING_ITEM_DIALOG } from 'src/testIds';
import { BookingItem } from '../../models/booking-item';
import { ProductService } from '../../shared';
import {
  HirePriceStructureDialogComponent,
  PriceStructureDialogResponse,
  HirePriceStructureDialogData
} from '../../shared/dialogs/hire-price-structure-dialog/hire-price-structure-dialog.component';
import { TestIdDirective } from '../../shared/directives/test-id.directive';
import { markFormGroupTouched } from '../../shared/helpers/markFormGroupTouched';
import { BookingItemService } from '../../shared/services/booking-item.service';
import { ConfigService } from '../../shared/services/config.service';
import { ProductCategoryService } from '../../shared/services/product-category.service';
import { AvailabilityComponent } from '../availability/conflict-alert/availability.component';
import { EditBookingItemStoreService } from './edit-booking-item-store.service';
import { PricingTypeLabelComponent } from '../../shared/pricing-type-label/pricing-type-label.component';
import { HlCurrencyPipe } from '../../shared/pipes/hl-currency.pipe';
import { PriceEstimateBreakdownPipe } from '../../shared/pipes/price-estimate-breakdown.pipe';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-edit-booking-item-dialog',
  templateUrl: './edit-booking-item-dialog.component.html',
  styleUrls: ['./edit-booking-item-dialog.component.css'],
  providers: [
    BookingItemService,
    ProductService,
    ProductCategoryService,
    EditBookingItemStoreService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' }
    },
    {
      provide: HIREABLE_PRICE_UPDATE_IJ,
      useClass: BookingItemPriceUpdateService
    }
  ],
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    FlexModule,
    MatFormField,
    MatLabel,
    MatSelect,
    TestIdDirective,
    NgFor,
    MatOption,
    NgIf,
    MatInput,
    AvailabilityComponent,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatError,
    MatDatepicker,
    MatIcon,
    MatSuffix,
    NgxMatTimepickerModule,
    MatButton,
    MatRadioGroup,
    MatRadioButton,
    CdkTextareaAutosize,
    MatDialogActions,
    AsyncPipe,
    MatHint,
    PricingTypeLabelComponent,
    HlCurrencyPipe,
    PriceEstimateBreakdownPipe,
    MatTooltip
  ]
})
export class EditBookingItemDialogComponent implements AfterViewInit {
  bookingItemForm = new UntypedFormGroup({
    category_id: new UntypedFormControl(null),
    product_id: new UntypedFormControl(),
    item_id: new UntypedFormControl(),
    quantity: new UntypedFormControl(null, Validators.required),
    on_hire: new UntypedFormControl(null, Validators.required),
    on_hire_time: new UntypedFormControl(null, Validators.required),
    off_hire: new UntypedFormControl(null, Validators.required),
    off_hire_time: new UntypedFormControl(null, Validators.required),
    notes: new UntypedFormControl(),
    collection: new UntypedFormControl(null, Validators.required),
    collectionLocation: new UntypedFormControl(null)
  });

  currentLocation$ = this.configService.currentLocation;
  testIds = EDIT_BOOKING_ITEM_DIALOG;
  locations$ = this.configService.locations;
  minimumOnHireDate = moment();
  maximumOnHireDate$ = this.getMaximumOnHireDate();
  minimumOffHireDate$ = this.getMinimumOffHireDate();
  categories$ = this.store.categories$;
  products$ = this.store.products$.pipe(
    tap((products) => {
      if (products.length === 1) {
        this.bookingItemForm.patchValue({
          product_id: products[0].id
        });
      }
    })
  );
  items$ = this.store.items$.pipe(
    tap((items) => {
      if (items.length === 1) {
        this.bookingItemForm.patchValue({
          item_id: items[0].id
        });
      }
    })
  );
  availableCount$ = this.store.availableCount$;
  suggestedPrice$ = this.store.suggestedPrice$;
  suggestedPriceError$ = this.store.suggestedPrice$.pipe(
    map((suggestedPrice) => {
      if (suggestedPrice?.error) {
        return suggestedPrice.error;
      }
      return null;
    })
  );

  calculatedPrice$ = this.suggestedPrice$.pipe(
    map((suggestedPrice) => {
      if (suggestedPrice?.estimate?.price) {
        return suggestedPrice.estimate.price;
      }
    })
  );
  isVirtualProduct$ = this.store.isVirtualProduct$;
  loading$ = this.store.loading$.pipe(
    startWith(true),
    tap((loading) => {
      if (loading) {
        this.bookingItemForm.disable();
      } else {
        this.bookingItemForm.enable();
      }
    })
  );
  pricingType: 'default' | 'fixed' | 'custom' | 'no_charge' = 'default';
  fixedPrice: number = null;
  zeroPriceReason: string = null;
  freeAccessory: boolean = null;

  constructor(
    public dialogRef: MatDialogRef<EditBookingItemDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private configService: ConfigService,
    private store: EditBookingItemStoreService,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef
  ) {
    this.setControlValues(data.item);
    this.store.setInitialBookingItem(data.item);
    this.store.listenForFormChanges(this.bookingItemForm);
    this.clearDependentFieldsOnParentChanged();
    markFormGroupTouched(this.bookingItemForm);
    // Initialize pricing state from item
    this.pricingType = data.item.pricing_type || 'default';
    this.fixedPrice = data.item.charge;
    this.freeAccessory = data.item.free_accessory;
    this.zeroPriceReason = data.item.zero_price_reason;
  }

  ngAfterViewInit() {
    this.bookingItemForm.controls.collectionLocation.disable();
    this.setCollectionLocationOnCollectionChange();
    this.disableItemOnVirtualProduct();
  }

  disableItemOnVirtualProduct() {
    this.store.isVirtualProduct$
      .pipe(takeUntil(this.store.destroy$))
      .subscribe((isVirtualProduct) => {
        if (isVirtualProduct) {
          this.bookingItemForm.controls.item_id.disable();
        } else {
          this.bookingItemForm.controls.item_id.enable();
        }
      });
  }

  convertFormToUpdate(): Observable<Partial<BookingItem>> {
    return this.suggestedPrice$.pipe(
      take(1),
      map((suggestedPrice) => {
        const formValue = this.bookingItemForm.value;
        return {
          category_id: formValue.category_id,
          product_id: formValue.product_id ? formValue.product_id : null,
          item_id: formValue.item_id ? formValue.item_id : null,
          quantity: formValue.quantity ? formValue.quantity : 1,
          location_id: formValue.collection ? formValue.collectionLocation : 0,
          charge:
            ['fixed', 'no_charge'].indexOf(this.pricingType) !== -1
              ? undefined
              : suggestedPrice?.estimate?.price,
          price_suggestion: suggestedPrice,
          notes: formValue.notes ? formValue.notes : '',
          on_hire: parse(
            formValue.on_hire_time,
            'HH:mm',
            formValue.on_hire.toDate()
          ),
          off_hire: parse(
            formValue.off_hire_time,
            'HH:mm',
            formValue.off_hire.toDate()
          )
        };
      })
    );
  }

  private setControlValues(item: BookingItem) {
    this.bookingItemForm.controls.quantity.disable();
    this.bookingItemForm.patchValue(
      {
        ...item,
        on_hire: moment(item.on_hire),
        on_hire_time: format(new Date(item.on_hire), 'HH:mm'),
        off_hire: moment(item.off_hire),
        off_hire_time: format(new Date(item.off_hire), 'HH:mm')
      },
      { emitEvent: false, onlySelf: true }
    );
    this.bookingItemForm.updateValueAndValidity();
    if (item.location_id) {
      this.bookingItemForm.controls.collection.patchValue(1);
      this.bookingItemForm.controls.collectionLocation.patchValue(
        item.location_id
      );
    } else if (item.location_id === 0) {
      this.bookingItemForm.controls.collection.patchValue(0);
      this.bookingItemForm.controls.collectionLocation.disable();
    }
  }

  private clearDependentFieldsOnParentChanged() {
    this.store.clearProductField
      .pipe(takeUntil(this.store.destroy$))
      .subscribe(() =>
        this.bookingItemForm.patchValue({ product_id: null, item_id: null })
      );
    this.store.clearItemField
      .pipe(takeUntil(this.store.destroy$))
      .subscribe(() => this.bookingItemForm.controls.item_id.patchValue(null));
  }

  private setCollectionLocationOnCollectionChange() {
    this.bookingItemForm.controls.collection.valueChanges
      .pipe(
        takeUntil(this.store.destroy$),
        mergeMap((collection) => {
          if (collection) {
            return this.configService.currentLocation.pipe(
              takeUntil(this.store.destroy$),
              map((location) => location?.id)
            );
          }
          return of(null);
        })
      )
      .subscribe((collectionLocation) => {
        this.bookingItemForm.controls.collectionLocation.patchValue(
          collectionLocation
        );
        if (collectionLocation) {
          this.bookingItemForm.controls.collectionLocation.enable();
        } else {
          this.bookingItemForm.controls.collectionLocation.disable();
        }
      });
  }

  private getMinimumOffHireDate() {
    return this.bookingItemForm.controls.on_hire.valueChanges.pipe(
      map((onHire) => {
        if (onHire && onHire.isValid()) {
          return onHire;
        }
        return this.minimumOnHireDate;
      })
    );
  }

  private getMaximumOnHireDate() {
    return this.bookingItemForm.controls.off_hire.valueChanges.pipe(
      map((offHire) => {
        if (offHire && offHire.isValid()) {
          return offHire;
        }
        return moment(this.minimumOnHireDate).add(1, 'day');
      })
    );
  }

  get pricingSummary() {
    if (this.pricingType === 'default') {
      return 'Default pricing structure';
    }
    if (this.pricingType === 'fixed') {
      return `Fixed price: ${this.bookingItemForm.get('charge').value}`;
    }
    if (this.pricingType === 'custom') {
      return 'Custom pricing structure';
    }
    if (this.pricingType === 'no_charge') {
      return 'No charge';
    }
    return '';
  }

  openPricingDetails() {
    this.convertFormToUpdate()
      .pipe(
        take(1),
        mergeMap((bookingItem: Partial<BookingItem>) => {
          this.store.saveBookingItem({
            bookingItem,
            onSuccess: () => {}
          });
          // wait until it has been saved
          return this.store.isClean$.pipe(
            filter((isClean) => isClean),
            mergeMap(() => this.store.priceStructureParams$),
            take(1)
          );
        }),
        mergeMap((priceStructureParams) => {
          return this.dialog
            .open<
              HirePriceStructureDialogComponent,
              HirePriceStructureDialogData,
              PriceStructureDialogResponse
            >(HirePriceStructureDialogComponent, {
              data: {
                productId: priceStructureParams.product_id,
                onHire: priceStructureParams.on_hire,
                offHire: priceStructureParams.off_hire,
                hireableId: priceStructureParams.hireable_id,
                hireableType: priceStructureParams.hireable_type,
                quantity: priceStructureParams.quantity,
                customerId: this.data.customerId,
                currency: this.data.currency || 'GBP',
                pricingType: this.pricingType,
                fixedPrice: this.fixedPrice,
                itemIsOffHired: true, // always update the price for booking items
                free_accessory: this.freeAccessory,
                zero_price_reason: this.zeroPriceReason,
                parentId: priceStructureParams.booking_id,
                currencyRate: this.data.currencyRate
              },
              viewContainerRef: this.viewContainerRef
            })
            .afterClosed();
        })
      )
      .subscribe((result) => {
        if (result) {
          this.pricingType = result.pricing_type;
          this.fixedPrice = result.charge;
          this.zeroPriceReason = result.zero_price_reason;
          this.freeAccessory = result.free_accessory;
          // if the pricing type is not fixed or no charge, update the suggested price
          if (['fixed', 'no_charge'].indexOf(this.pricingType) === -1) {
            this.store.triggerSuggestedPriceUpdate.next();
          }
        }
      });
  }

  save(closeOnSuccess: boolean = true) {
    this.convertFormToUpdate()
      .pipe(take(1))
      .subscribe((bookingItem) => {
        this.store.saveBookingItem({
          bookingItem,
          onSuccess: () => {
            if (closeOnSuccess) {
              this.dialogRef.close();
            }
          }
        });
      });
  }

  updateTime(event: any, field: string) {
    this.bookingItemForm.get(field).patchValue(event);
  }

  updateTimeEnteredManually(field: string, picker: NgxMatTimepickerComponent) {
    const control = this.bookingItemForm.get(field);
    if (!control.value) {
      return;
    }
    // 24 hr patterns to try and parse
    const parsePatterns = ['HH:mm', 'HH.mm', 'H:mm', 'HHmm', 'Hmm', 'HH', 'H'];
    const invalidValue = control.value;
    for (const pattern of parsePatterns) {
      const parsed = parse(invalidValue, pattern, new Date());
      if (parsed.toString() !== 'Invalid Date') {
        control.patchValue(format(parsed, 'HH:mm'));
        return picker.updateTime(control.value);
      }
    }
    control.setErrors({ invalidTime: true });
  }
}
